// src/pages/AISentry.jsx
// AI-Enhanced Network Topology - Proactive Protection
// Powered by AI Sentry - With Nimbus Integration for Autonomous Actions

import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import ReactMarkdown from 'react-markdown';
import { edgeNodesService } from '../services/edge-nodes';
import { eventsService } from '../services/events';
import api from '../services/api';

// ════════════════════════════════════════════════════════════════════════════
// MARKDOWN STYLING COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

const MarkdownComponents = {
  h1: ({ children }) => <h1 className="text-lg font-bold text-white mb-3 mt-4 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-bold text-cyan-300 mb-2 mt-4 first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold text-cyan-200 mb-2 mt-3">{children}</h3>,
  p: ({ children }) => <p className="text-slate-200 text-sm mb-2 leading-relaxed">{children}</p>,
  strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
  ul: ({ children }) => <ul className="list-disc list-inside text-slate-200 text-sm mb-2 space-y-1 ml-2">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside text-slate-200 text-sm mb-2 space-y-1 ml-2">{children}</ol>,
  li: ({ children }) => <li className="text-slate-200">{children}</li>,
  hr: () => <hr className="border-slate-600 my-3" />,
  table: ({ children }) => (
    <div className="overflow-x-auto mb-3 rounded-lg border border-slate-600">
      <table className="min-w-full text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-700/50">{children}</thead>,
  tbody: ({ children }) => <tbody className="divide-y divide-slate-600">{children}</tbody>,
  tr: ({ children }) => <tr className="hover:bg-slate-700/30">{children}</tr>,
  th: ({ children }) => <th className="px-2 py-1.5 text-left font-semibold text-cyan-300 whitespace-nowrap">{children}</th>,
  td: ({ children }) => <td className="px-2 py-1.5 text-slate-200 whitespace-nowrap">{children}</td>,
  code: ({ children }) => <code className="bg-slate-700 px-1 py-0.5 rounded text-cyan-300 text-xs font-mono">{children}</code>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-cyan-500 pl-3 my-2 text-slate-300 italic">{children}</blockquote>,
};

// ════════════════════════════════════════════════════════════════════════════
// AI STATUS TYPES
// ════════════════════════════════════════════════════════════════════════════

const AI_STATUS = {
  ISOLATED: { icon: '🛡️', text: 'ISOLATED', bg: 'bg-red-500', glow: 'from-red-500/50 to-rose-500/50' },
  ANALYZING: { icon: '🔍', text: 'ANALYZING', bg: 'bg-purple-500', glow: 'from-purple-500/50 to-violet-500/50', pulse: true },
  ALERT: { icon: '⚠️', text: 'ALERT', bg: 'bg-yellow-500', glow: 'from-yellow-500/50 to-amber-500/50' },
  PROTECTED: { icon: '🤖', text: 'PROTECTED', bg: 'bg-emerald-500', glow: 'from-emerald-500/30 to-teal-500/30' },
};

// ════════════════════════════════════════════════════════════════════════════
// NIMBUS API INTEGRATION - Execute actions on Stratus routers
// ════════════════════════════════════════════════════════════════════════════

const executeNimbusAction = async (actionType, params) => {
  try {
    const response = await api.post('/nimbus/action/', {
      action_type: actionType,
      params: params
    });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Nimbus action failed:', error);
    return { success: false, error: error.message };
  }
};

// ════════════════════════════════════════════════════════════════════════════
// HEURISTIC DETECTION (for visual badges) + VPN RECOMMENDATIONS
// ════════════════════════════════════════════════════════════════════════════

const detectThreatsHeuristic = (events, edgeNodes) => {
  const threats = [];
  const bySource = {};
  
  events.forEach(event => {
    const sourceIp = event.source_ip;
    const destIp = event.destination_ip;
    const severity = event.severity;
    const policyName = event.details?.policy_name || '';
    
    if (!sourceIp || sourceIp === 'N/A' || !destIp || destIp === 'N/A') return;
    
    if (!bySource[sourceIp]) {
      bySource[sourceIp] = { targets: new Set(), criticalCount: 0, policies: new Set(), events: [], agentId: null };
    }
    
    bySource[sourceIp].targets.add(destIp);
    bySource[sourceIp].events.push(event);
    if (policyName) bySource[sourceIp].policies.add(policyName);
    if (severity === 'Critical') bySource[sourceIp].criticalCount++;
    if (event.celerway_edge_node_id) bySource[sourceIp].agentId = event.celerway_edge_node_id;
  });

  Object.entries(bySource).forEach(([sourceIp, data]) => {
    const policies = Array.from(data.policies);
    const hasUnauthorized = policies.some(p => 
      p.toLowerCase().includes('unauthorized') || p.toLowerCase().includes('critical infrastructure')
    );
    
    if (data.criticalCount > 0 && hasUnauthorized) {
      // Add ISOLATION threat
      threats.push({
        id: `isolation-${sourceIp}`,
        type: 'ISOLATION',
        device: sourceIp,
        targetDevices: Array.from(data.targets),
        reason: 'Unauthorized access blocked',
        confidence: Math.min(98, 85 + data.criticalCount * 3),
        severity: 'critical',
        agentId: data.agentId,
      });
      
      // Add NETWORK_RESPONSE action for isolation threats (assigns tag to router)
      if (data.agentId) {
        threats.push({
          id: `response-${sourceIp}`,
          type: 'VPN_ACTION',  // Keep type for compatibility
          device: sourceIp,
          targetDevices: Array.from(data.targets),
          reason: 'Recommend network isolation response',
          confidence: 95,
          severity: 'critical',
          agentId: data.agentId,
          actionDetails: {
            actionType: 'Tag Assignment',
            description: 'Apply security tag to router via Nimbus API',
            tagPrefix: 'aisentry-isolated',
            visibleIn: 'Nimbus Portal'
          }
        });
      }
    } else if (data.targets.size >= 3 && data.events.length >= 10) {
      threats.push({
        id: `scan-${sourceIp}`,
        type: 'RECOMMENDATION',
        device: sourceIp,
        targetDevices: Array.from(data.targets),
        reason: 'Port scan pattern detected',
        confidence: Math.min(90, 70 + data.targets.size * 4),
        severity: 'high',
        agentId: data.agentId,
      });
    }
  });

  return threats.sort((a, b) => {
    const sevOrder = { critical: 0, high: 1, medium: 2 };
    return (sevOrder[a.severity] || 3) - (sevOrder[b.severity] || 3);
  });
};

const getDeviceAIStatus = (deviceIp, threats) => {
  const asSource = threats.find(t => t.device === deviceIp);
  if (asSource) {
    if (asSource.type === 'ISOLATION') return AI_STATUS.ISOLATED;
    if (asSource.type === 'RECOMMENDATION') return AI_STATUS.ALERT;
  }
  const asTarget = threats.find(t => t.targetDevices?.includes(deviceIp));
  if (asTarget && asTarget.type === 'ISOLATION') return AI_STATUS.ANALYZING;
  return AI_STATUS.PROTECTED;
};

// ════════════════════════════════════════════════════════════════════════════
// AI Sentry API CALLS
// ════════════════════════════════════════════════════════════════════════════

const generateAIBriefing = async () => {
  try {
    const prompt = `Generate a brief shift change security briefing for Brian (2-3 sentences max). 
Start with "Attention needed:" if there are critical threats, or "All clear:" if the network is secure.
Focus on the most important security concern right now. Be direct and actionable.`;

    const response = await api.post('/chat/', {
      message: prompt,
      history: [],
      session_id: `briefing-${Date.now()}`
    });
    
    const content = response.data.content;
    if (content && content.includes('encountered an error')) return null;
    return content || null;
  } catch (error) {
    console.log('AI briefing error:', error.message);
    return null;
  }
};

const generateExpandedBriefing = async () => {
  try {
    const prompt = `Generate a detailed security analysis report. Include:
1. **Threat Summary**: What attacks or suspicious activity are happening?
2. **Affected Assets**: Which PLCs, devices, or network segments are involved?
3. **Attack Timeline**: When did this activity start and what's the pattern?
4. **Risk Assessment**: How critical is this situation?
5. **Recommended Actions**: What should Brian do right now?

Be specific with IP addresses, protocols, and timestamps from the event data.`;

    const response = await api.post('/chat/', {
      message: prompt,
      history: [],
      session_id: `expanded-${Date.now()}`
    });
    
    const content = response.data.content;
    if (content && content.includes('encountered an error')) return null;
    return content || null;
  } catch (error) {
    console.log('Expanded briefing error:', error.message);
    return null;
  }
};

// ════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ════════════════════════════════════════════════════════════════════════════

// Check if node is online based on heartbeat (15 min threshold)
// Uses UTC comparison to work correctly across all timezones
const isNodeOnline = (node) => {
  if (!node.last_heartbeat) return false;
  
  // Ensure timestamp is parsed as UTC
  let heartbeatStr = node.last_heartbeat;
  if (!heartbeatStr.endsWith('Z') && !heartbeatStr.includes('+')) {
    heartbeatStr += 'Z';  // Treat as UTC if no timezone specified
  }
  
  const lastHeartbeat = new Date(heartbeatStr).getTime();
  const nowUTC = Date.now();  // Always returns UTC milliseconds
  const diffMinutes = (nowUTC - lastHeartbeat) / (1000 * 60);
  
  return diffMinutes <= 15;  // 15 minute threshold
};

// Calculate status from heartbeat (not from backend status field)
const getNodeStatus = (node) => {
  return isNodeOnline(node) ? 'Online' : 'Offline';
};

// ════════════════════════════════════════════════════════════════════════════
// CUSTOM NODE COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

const CloudNode = ({ data }) => (
  <div className="relative group">
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-cyan-400 !border-2 !border-white" />
    <div className="absolute -inset-2 bg-gradient-to-r from-cyan-400/30 via-blue-400/30 to-purple-400/30 rounded-2xl blur-xl opacity-75" />
    <div className="relative bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 border-2 border-cyan-400/50 rounded-2xl p-6 min-w-[200px] shadow-2xl">
      <div className="absolute -top-1.5 -right-1.5 w-4 h-4">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-400" />
      </div>
      <div className="flex items-center justify-center mb-3">
        <div className="w-14 h-14 bg-gradient-to-br from-cyan-400/30 to-blue-500/30 rounded-xl flex items-center justify-center border border-cyan-400/50">
          <svg className="w-8 h-8 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
          </svg>
        </div>
      </div>
      <div className="text-center">
        <div className="text-sm font-semibold text-white">{data.label}</div>
        <div className="text-xs text-slate-300 mt-1">{data.sublabel}</div>
      </div>
      <div className="mt-4 pt-3 border-t border-slate-600/50 flex justify-center gap-4">
        <div className="text-center">
          <div className="text-lg font-bold text-emerald-300">{data.edgeCount || 0}</div>
          <div className="text-[10px] text-slate-400 uppercase">Agents</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-blue-300">{data.assetCount || 0}</div>
          <div className="text-[10px] text-slate-400 uppercase">Devices</div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-center gap-2">
        <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-[10px] text-emerald-300 font-medium">AI Sentry Active</span>
      </div>
    </div>
  </div>
);

const EdgeGatewayNode = ({ data }) => {
  const isOnline = data.status === 'Online';
  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-emerald-400 !border-2 !border-white" />
      <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-blue-400 !border-2 !border-white" />
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 ${isOnline ? 'border-emerald-400/60' : 'border-red-400/60'} rounded-xl p-4 min-w-[220px] shadow-xl transition-all group-hover:scale-105`}>
        <div className="absolute -top-1 -right-1 w-3 h-3">
          {isOnline && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />}
          <span className={`relative inline-flex rounded-full h-3 w-3 ${isOnline ? 'bg-emerald-400' : 'bg-red-400'}`} />
        </div>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isOnline ? 'bg-emerald-500/30' : 'bg-red-500/30'}`}>
            <svg className={`w-5 h-5 ${isOnline ? 'text-emerald-300' : 'text-red-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white truncate">{data.nodeId}</div>
            {data.hostname && data.hostname !== data.nodeId && (
              <div className="text-[10px] text-slate-400 font-mono truncate">{data.hostname}</div>
            )}
          </div>
        </div>
        <div className="mt-3 pt-2 border-t border-slate-500/30 flex justify-between text-xs">
          <div>
            <span className="text-slate-400">Status</span>
            <div className={`font-semibold ${isOnline ? 'text-emerald-300' : 'text-red-300'}`}>{data.status}</div>
          </div>
          <div className="text-right">
            <span className="text-slate-400">Devices</span>
            <div className="font-semibold text-blue-300">{data.deviceCount || 0}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DeviceNode = ({ data }) => {
  const protocolColors = {
    modbus: { bg: 'from-orange-400/30 to-amber-500/30', text: 'text-orange-300', badge: 'bg-orange-500/30 text-orange-200 border-orange-400/50' },
    opcua: { bg: 'from-purple-400/30 to-violet-500/30', text: 'text-purple-300', badge: 'bg-purple-500/30 text-purple-200 border-purple-400/50' },
    tcp: { bg: 'from-blue-400/30 to-cyan-500/30', text: 'text-blue-300', badge: 'bg-blue-500/30 text-blue-200 border-blue-400/50' },
    default: { bg: 'from-slate-400/30 to-gray-500/30', text: 'text-slate-300', badge: 'bg-slate-500/30 text-slate-200 border-slate-400/50' },
  };
  
  const primaryProtocol = data.protocols?.[0] || 'default';
  const colors = protocolColors[primaryProtocol] || protocolColors.default;
  const aiStatus = data.aiStatus || AI_STATUS.PROTECTED;
  const hasViolations = data.policyViolations > 0;
  
  const getBorderClass = () => {
    if (aiStatus === AI_STATUS.ISOLATED) return 'border-red-500 border-2';
    if (aiStatus === AI_STATUS.ANALYZING) return 'border-purple-500 border-2';
    if (aiStatus === AI_STATUS.ALERT) return 'border-yellow-500 border-2';
    if (hasViolations) return 'border-red-400';
    return 'border-slate-500/50';
  };

  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-orange-400 !border-2 !border-white" />
      
      {aiStatus !== AI_STATUS.PROTECTED && (
        <div className={`absolute -inset-2 bg-gradient-to-r ${aiStatus.glow} rounded-xl blur-lg opacity-60 ${aiStatus.pulse ? 'animate-pulse' : ''}`} />
      )}
      
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 ${getBorderClass()} rounded-lg p-3 min-w-[160px] shadow-lg transition-all group-hover:scale-105 ${aiStatus.pulse ? 'animate-pulse' : ''}`}>
        
        <div className={`absolute -top-2 -right-2 px-2 py-0.5 ${aiStatus.bg} text-white text-[9px] font-bold rounded-full shadow-lg flex items-center gap-1`}>
          <span>{aiStatus.icon}</span>
          <span>{aiStatus.text}</span>
        </div>
        
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br ${colors.bg}`}>
            <svg className={`w-4 h-4 ${colors.text}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
          </div>
          <div>
            <div className="text-xs font-semibold text-white">{data.role || 'Device'}</div>
            <div className="text-[9px] text-slate-300">DISCOVERED</div>
          </div>
        </div>
        
        <div className="bg-slate-800/60 rounded px-2 py-1.5 mb-2">
          <div className={`text-sm font-mono font-semibold ${
            aiStatus === AI_STATUS.ISOLATED ? 'text-red-300' :
            aiStatus === AI_STATUS.ALERT ? 'text-yellow-300' : 'text-cyan-300'
          }`}>
            {data.ip}
          </div>
        </div>
        
        {data.protocols && data.protocols.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {data.protocols.map((protocol, idx) => {
              const pColor = protocolColors[protocol] || protocolColors.default;
              return (
                <span key={idx} className={`text-[9px] px-1.5 py-0.5 rounded border font-semibold uppercase ${pColor.badge}`}>
                  {protocol}
                </span>
              );
            })}
          </div>
        )}
        
        <div className="text-[10px] text-slate-300 text-center">
          {data.eventCount || 0} events
        </div>
      </div>
    </div>
  );
};

const nodeTypes = { cloud: CloudNode, edgeGateway: EdgeGatewayNode, device: DeviceNode };

// ════════════════════════════════════════════════════════════════════════════
// SHIFT BRIEFING MODAL
// ════════════════════════════════════════════════════════════════════════════

const ShiftBriefingModal = ({ isOpen, onClose, aiBriefing, stats, threats, isLoadingAI, onRequestExpanded, expandedBriefing, isLoadingExpanded }) => {
  const [showExpanded, setShowExpanded] = useState(false);
  
  if (!isOpen) return null;
  
  const isolatedCount = threats.filter(t => t.type === 'ISOLATION').length;
  const hasCritical = isolatedCount > 0;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-600 rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`bg-gradient-to-r ${hasCritical ? 'from-red-600/20 to-orange-600/20' : 'from-cyan-600/20 to-blue-600/20'} border-b border-slate-700 px-6 py-4 flex-shrink-0`}>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 ${hasCritical ? 'bg-red-500/30' : 'bg-cyan-500/30'} rounded-xl flex items-center justify-center`}>
              <span className="text-2xl">{hasCritical ? '🚨' : '🤖'}</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Shift Briefing</h2>
              <p className="text-slate-400 text-sm">AI Sentry Security Summary</p>
            </div>
          </div>
        </div>
        
        {/* Scrollable Content */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-700/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-emerald-300">{stats.totalDevices || 0}</div>
              <div className="text-xs text-slate-400">Devices</div>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-red-300">{isolatedCount}</div>
              <div className="text-xs text-slate-400">Threats</div>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-blue-300">{stats.alertCount || 0}</div>
              <div className="text-xs text-slate-400">Events</div>
            </div>
          </div>
          
          {/* AI Briefing */}
          <div className={`rounded-xl p-4 ${hasCritical ? 'bg-red-900/20 border border-red-500/30' : 'bg-slate-700/30'}`}>
            {isLoadingAI ? (
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
                <span className="text-slate-300 text-sm">AI Sentry is analyzing your network...</span>
              </div>
            ) : aiBriefing ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">🧠</span>
                  <span className="text-sm font-semibold text-cyan-300">AI Analysis</span>
                </div>
                <p className="text-white text-sm leading-relaxed">{aiBriefing}</p>
              </>
            ) : (
              <>
                <p className="text-white font-medium mb-2">
                  {hasCritical ? '⚠️ Attention needed:' : '✅ Status:'}
                </p>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {hasCritical 
                    ? `Detected ${isolatedCount} threat source${isolatedCount > 1 ? 's' : ''} attempting unauthorized access.`
                    : `All ${stats.totalDevices || 0} devices operating normally.`
                  }
                </p>
              </>
            )}
          </div>
          
          {/* Expanded Briefing Toggle */}
          {aiBriefing && (
            <div>
              {!showExpanded ? (
                <button
                  onClick={() => {
                    setShowExpanded(true);
                    if (!expandedBriefing) onRequestExpanded();
                  }}
                  className="w-full py-2 text-sm text-cyan-300 hover:text-cyan-200 flex items-center justify-center gap-2 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  Show Expanded Analysis
                </button>
              ) : (
                <div className="bg-slate-700/30 rounded-xl p-4 border border-cyan-500/30">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">📊</span>
                      <span className="text-sm font-semibold text-cyan-300">Expanded Analysis</span>
                    </div>
                    <button onClick={() => setShowExpanded(false)} className="text-slate-400 hover:text-white text-xs">
                      Collapse ▲
                    </button>
                  </div>
                  {isLoadingExpanded ? (
                    <div className="flex items-center gap-3 py-4">
                      <div className="w-5 h-5 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
                      <span className="text-slate-300 text-sm">Generating detailed analysis...</span>
                    </div>
                  ) : expandedBriefing ? (
                    <div className="max-h-80 overflow-y-auto pr-2 prose-sm">
                      <ReactMarkdown components={MarkdownComponents}>
                        {expandedBriefing}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-slate-400 text-sm">Unable to generate expanded analysis.</p>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Threat List */}
          {threats.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-400 mb-2">Detected Threats</h3>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {threats.slice(0, 3).map(threat => (
                  <div key={threat.id} className="flex items-center gap-3 bg-slate-700/30 rounded-lg p-2">
                    <span className="text-lg">{threat.type === 'ISOLATION' ? '🛡️' : '⚠️'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{threat.reason}</div>
                      <div className="text-xs text-slate-400">{threat.device} → {threat.targetDevices?.length || 0} target(s)</div>
                    </div>
                    <div className="text-xs text-slate-500">{threat.confidence}%</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="bg-slate-800/50 border-t border-slate-700 px-6 py-4 flex justify-between items-center flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-md flex items-center justify-center">
              <span className="text-xs font-bold text-white">e</span>
            </div>
            <span className="text-xs text-slate-400">Powered by <span className="text-cyan-400 font-semibold">AI Sentry</span></span>
          </div>
          <button onClick={onClose} className={`px-6 py-2 ${hasCritical ? 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500' : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500'} text-white font-semibold rounded-lg transition-all`}>
            {hasCritical ? 'Review Threats' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PROACTIVE ALERT - LEFT SIDE
// ════════════════════════════════════════════════════════════════════════════

const ProactiveAlert = ({ threats, onViewDetails }) => {
  const criticalThreats = threats.filter(t => t.severity === 'critical');
  if (criticalThreats.length === 0) return null;
  
  const top = criticalThreats[0];
  
  return (
    <div className="absolute left-6 top-[25%] z-30 animate-slide-in-left">
      <div className="bg-gradient-to-r from-red-900/95 to-rose-900/95 backdrop-blur-xl border border-red-500/50 rounded-2xl shadow-2xl p-4 max-w-sm">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-red-500/30 rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-xl">🚨</span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-red-300 font-bold text-sm">Brian, you need to see this</span>
            </div>
            <p className="text-white text-sm font-medium">{top.reason}</p>
            <p className="text-red-200/70 text-xs mt-1">
              {top.device} → {top.targetDevices?.length || 0} target(s)
            </p>
            <button onClick={onViewDetails} className="mt-3 px-4 py-1.5 bg-red-500 hover:bg-red-400 text-white text-xs font-semibold rounded-lg transition-colors">
              View Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// AI PANEL - Shows heuristic detections, VPN actions, and recent events
// ════════════════════════════════════════════════════════════════════════════

const AIPanel = ({ isOpen, onClose, threats, recentEvents, onRefresh, isRefreshing, onApproveVPN }) => {
  const [activeTab, setActiveTab] = useState('detections');
  const [executingAction, setExecutingAction] = useState(null);
  const [actionResults, setActionResults] = useState({});
  
  if (!isOpen) return null;
  
  // Get critical/warning events from recent events
  const criticalEvents = recentEvents?.filter(e => e.severity === 'Critical').slice(0, 10) || [];
  const warningEvents = recentEvents?.filter(e => e.severity === 'Warning').slice(0, 10) || [];
  
  // Separate VPN actions from other threats
  const vpnActions = threats.filter(t => t.type === 'VPN_ACTION');
  const otherThreats = threats.filter(t => t.type !== 'VPN_ACTION');
  
  const handleApproveVPN = async (action) => {
    setExecutingAction(action.id);
    try {
      const result = await onApproveVPN(action);
      setActionResults(prev => ({ ...prev, [action.id]: result }));
    } catch (error) {
      setActionResults(prev => ({ ...prev, [action.id]: { success: false, error: error.message } }));
    } finally {
      setExecutingAction(null);
    }
  };
  
  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-[420px] bg-slate-900 border-l border-slate-700 z-50 shadow-2xl overflow-hidden flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 border-b border-slate-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-xl">🤖</span>
            <div>
              <h2 className="text-white font-bold">AI Sentry Activity</h2>
              <p className="text-slate-400 text-xs">{threats.length} detections • {criticalEvents.length + warningEvents.length} events</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={onRefresh}
              disabled={isRefreshing}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh data"
            >
              <svg className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg">✕</button>
          </div>
        </div>
        
        {/* Status Bar */}
        <div className="px-6 py-3 bg-emerald-500/10 border-b border-emerald-500/30 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-emerald-300 text-sm font-medium">Monitoring Active</span>
            </div>
            <span className="text-slate-500 text-xs">Nimbus Integration Ready</span>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-slate-700 flex-shrink-0">
          <button
            onClick={() => setActiveTab('detections')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'detections' 
                ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            🛡️ Detections ({otherThreats.length})
          </button>
          <button
            onClick={() => setActiveTab('actions')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'actions' 
                ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            ⚡ Actions ({vpnActions.length})
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'events' 
                ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            📊 Events
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {activeTab === 'detections' ? (
            // Heuristic Detections Tab
            otherThreats.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-3xl">✅</span>
                <p className="text-slate-300 font-medium mt-4">All Clear</p>
                <p className="text-slate-500 text-sm mt-1">No threat patterns detected</p>
              </div>
            ) : (
              otherThreats.map(threat => (
                <div key={threat.id} className={`border-2 rounded-xl p-4 ${
                  threat.type === 'ISOLATION' ? 'border-red-500 bg-red-500/10' : 'border-yellow-500 bg-yellow-500/10'
                }`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{threat.type === 'ISOLATION' ? '🛡️' : '⚠️'}</span>
                      <div>
                        <div className="text-white font-semibold text-sm">{threat.type}</div>
                        <div className="text-slate-400 text-xs">Heuristic Pattern</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-slate-400 text-[10px]">Confidence</div>
                      <div className="text-white font-bold">{threat.confidence}%</div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <span className="font-mono text-sm bg-slate-800 px-2 py-1 rounded text-cyan-300">{threat.device}</span>
                  </div>
                  <div>
                    <div className="text-white text-sm font-medium">{threat.reason}</div>
                    <div className="text-slate-400 text-xs mt-1">
                      Targeting: {threat.targetDevices?.slice(0, 3).join(', ')}{threat.targetDevices?.length > 3 ? ` +${threat.targetDevices.length - 3} more` : ''}
                    </div>
                  </div>
                </div>
              ))
            )
          ) : activeTab === 'actions' ? (
            // Network Response Actions Tab
            vpnActions.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-3xl">⚡</span>
                <p className="text-slate-300 font-medium mt-4">No Pending Actions</p>
                <p className="text-slate-500 text-sm mt-1">AI Sentry will recommend actions when threats are detected</p>
              </div>
            ) : (
              vpnActions.map(action => {
                const result = actionResults[action.id];
                const isExecuting = executingAction === action.id;
                
                return (
                  <div key={action.id} className="border-2 border-purple-500 bg-purple-500/10 rounded-xl p-4">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">🔒</span>
                        <div>
                          <div className="text-white font-semibold text-sm">Network Response</div>
                          <div className="text-purple-300 text-xs">Nimbus API Action</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-slate-400 text-[10px]">Confidence</div>
                        <div className="text-white font-bold">{action.confidence}%</div>
                      </div>
                    </div>
                    
                    {/* Target Router */}
                    <div className="mb-3 p-2 bg-slate-800/50 rounded-lg">
                      <div className="text-[10px] text-slate-400 uppercase mb-1">Target Router</div>
                      <div className="font-mono text-sm text-cyan-300">{action.agentId || 'Stratus5G_5728_Austin'}</div>
                    </div>
                    
                    {/* Action Details - Tag Assignment */}
                    <div className="mb-3 p-3 bg-slate-800/80 rounded-lg border border-purple-500/30">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">🏷️</span>
                        <span className="text-white text-sm font-medium">Security Tag Assignment</span>
                      </div>
                      <div className="bg-slate-900/50 rounded px-3 py-2 font-mono text-sm text-purple-300">
                        aisentry-isolated-{new Date().toISOString().slice(0,10).replace(/-/g,'')}
                      </div>
                      <div className="text-slate-400 text-xs mt-2 flex items-center gap-1">
                        <span>👁️</span> Visible in Nimbus Portal
                      </div>
                    </div>
                    
                    {/* Threat Info */}
                    <div className="mb-4 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <div className="text-xs text-red-300 font-medium mb-1">Responding to Threat:</div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-red-400">{action.device}</span>
                        <span className="text-slate-500">→</span>
                        <span className="text-slate-300 text-sm">{action.targetDevices?.length || 0} PLCs targeted</span>
                      </div>
                    </div>
                    
                    {/* Result Display */}
                    {result && (
                      <div className={`mb-3 p-3 rounded-lg ${result.success ? 'bg-emerald-500/20 border border-emerald-500/50' : 'bg-red-500/20 border border-red-500/50'}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{result.success ? '✅' : '❌'}</span>
                          <div>
                            <div className={`text-sm font-medium ${result.success ? 'text-emerald-300' : 'text-red-300'}`}>
                              {result.success ? 'Tag Applied Successfully!' : `Failed: ${result.error}`}
                            </div>
                            {result.success && result.data?.tag_name && (
                              <div className="text-emerald-200/70 text-xs mt-0.5 font-mono">{result.data.tag_name}</div>
                            )}
                          </div>
                        </div>
                        {result.success && (
                          <p className="text-emerald-200/70 text-xs mt-2">✨ Check Nimbus portal - tag should appear on router</p>
                        )}
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    {!result?.success && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApproveVPN(action)}
                          disabled={isExecuting}
                          className="flex-1 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 disabled:from-slate-600 disabled:to-slate-600 text-white text-sm font-semibold rounded-lg transition-all flex items-center justify-center gap-2"
                        >
                          {isExecuting ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              Sending to Nimbus...
                            </>
                          ) : (
                            <>
                              <span>✓</span>
                              Approve & Execute
                            </>
                          )}
                        </button>
                        <button
                          disabled={isExecuting}
                          className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-300 text-sm font-medium rounded-lg transition-colors"
                        >
                          Dismiss
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )
          ) : (
            // Events Tab
            <>
              {/* Critical Events */}
              {criticalEvents.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    Critical Events ({criticalEvents.length})
                  </h3>
                  <div className="space-y-2">
                    {criticalEvents.map((event, idx) => (
                      <div key={idx} className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                        <div className="flex items-start justify-between mb-1">
                          <span className="text-xs text-red-300 font-medium">{event.details?.policy_name || 'Policy Match'}</span>
                          <span className="text-[10px] text-slate-500">
                            {event.timestamp ? new Date(event.timestamp).toLocaleTimeString() : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-mono text-slate-300">{event.source_ip || 'N/A'}</span>
                          <span className="text-slate-500">→</span>
                          <span className="font-mono text-cyan-300">{event.destination_ip || 'N/A'}</span>
                        </div>
                        <div className="text-[10px] text-slate-400 mt-1 truncate">
                          {event.description || event.details?.rule_name || 'Security event detected'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Warning Events */}
              {warningEvents.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-xs font-semibold text-yellow-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full" />
                    Warning Events ({warningEvents.length})
                  </h3>
                  <div className="space-y-2">
                    {warningEvents.map((event, idx) => (
                      <div key={idx} className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                        <div className="flex items-start justify-between mb-1">
                          <span className="text-xs text-yellow-300 font-medium">{event.details?.policy_name || 'Policy Match'}</span>
                          <span className="text-[10px] text-slate-500">
                            {event.timestamp ? new Date(event.timestamp).toLocaleTimeString() : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-mono text-slate-300">{event.source_ip || 'N/A'}</span>
                          <span className="text-slate-500">→</span>
                          <span className="font-mono text-cyan-300">{event.destination_ip || 'N/A'}</span>
                        </div>
                        <div className="text-[10px] text-slate-400 mt-1 truncate">
                          {event.description || event.details?.rule_name || 'Security event detected'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* No Events */}
              {criticalEvents.length === 0 && warningEvents.length === 0 && (
                <div className="text-center py-12">
                  <span className="text-3xl">📭</span>
                  <p className="text-slate-300 font-medium mt-4">No Recent Alerts</p>
                  <p className="text-slate-500 text-sm mt-1">Click refresh to fetch latest events</p>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gradient-to-br from-cyan-400 to-blue-500 rounded flex items-center justify-center">
              <span className="text-[10px] font-bold text-white">e</span>
            </div>
            <span className="text-xs text-slate-500">Powered by <span className="text-cyan-400">AI Sentry</span></span>
          </div>
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="text-xs text-cyan-400 hover:text-cyan-300 disabled:opacity-50"
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh Now'}
          </button>
        </div>
      </div>
    </>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// EDGE NODE FILTER
// ════════════════════════════════════════════════════════════════════════════

const EdgeNodeFilter = ({ edgeNodes, selectedAgent, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedNode = selectedAgent ? edgeNodes.find(n => n.edge_node_id === selectedAgent) : null;
  
  return (
    <div className="relative">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 bg-slate-700/80 hover:bg-slate-600/80 border border-slate-500/50 rounded-lg px-4 py-2 text-sm text-white min-w-[280px]">
        <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
        <span className="flex-1 text-left truncate">{selectedNode ? selectedNode.edge_node_id : 'All Edge Nodes'}</span>
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-1 w-full bg-slate-800 border border-slate-600/50 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
            <button onClick={() => { onSelect(null); setIsOpen(false); }} className={`w-full px-4 py-3 text-left text-sm hover:bg-slate-700/50 ${!selectedAgent ? 'bg-cyan-500/20 text-cyan-300' : 'text-slate-200'}`}>
              All Edge Nodes
            </button>
            {[...edgeNodes].sort((a, b) => (a.edge_node_id || '').localeCompare(b.edge_node_id || '')).map(node => {
              // Calculate online status from heartbeat (UTC-safe, 15 min threshold)
              const isOnline = isNodeOnline(node);
              const status = isOnline ? 'Online' : 'Offline';
              return (
                <button key={node.edge_node_id} onClick={() => { onSelect(node.edge_node_id); setIsOpen(false); }} className={`w-full px-4 py-3 text-left hover:bg-slate-700/50 ${selectedAgent === node.edge_node_id ? 'bg-cyan-500/20' : ''}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full ${isOnline ? 'bg-emerald-400' : 'bg-red-400'}`} />
                    <span className={`text-sm font-medium ${selectedAgent === node.edge_node_id ? 'text-cyan-300' : 'text-white'}`}>{node.edge_node_id}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ml-auto ${isOnline ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>{status}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// STATS BAR WITH BRIEFING BUTTON
// ════════════════════════════════════════════════════════════════════════════

const StatsBar = ({ stats, threatsCount, onOpenPanel, onRequestBriefing }) => (
  <div className="flex items-center gap-4">
    {/* On-demand Briefing Button */}
    <button
      onClick={onRequestBriefing}
      className="flex items-center gap-2 bg-gradient-to-r from-cyan-600/20 to-blue-600/20 hover:from-cyan-600/30 hover:to-blue-600/30 border border-cyan-500/50 rounded-lg px-4 py-2 transition-all"
    >
      <span className="text-lg">📋</span>
      <span className="text-sm font-medium text-cyan-300">Briefing</span>
    </button>
    
    <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
      <span className="text-emerald-300 font-bold text-lg">{stats.onlineAgents || 0}</span>
      <span className="text-slate-300 text-xs">/ {stats.totalAgents || 0} Online</span>
    </div>
    <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
      <span className="text-blue-300 font-bold text-lg">{stats.totalDevices || 0}</span>
      <span className="text-slate-300 text-xs">Devices</span>
    </div>
    {(stats.alertCount || 0) > 0 && (
      <div className="flex items-center gap-2 bg-red-500/20 rounded-lg px-3 py-2 border border-red-400/50 animate-pulse">
        <span className="text-red-300 font-bold text-lg">{stats.alertCount}</span>
        <span className="text-red-300/70 text-xs">Alerts</span>
      </div>
    )}
    <button onClick={onOpenPanel} className={`flex items-center gap-2 rounded-lg px-4 py-2 border transition-all ${threatsCount > 0 ? 'bg-purple-500/20 border-purple-400/50 hover:bg-purple-500/30' : 'bg-emerald-500/20 border-emerald-400/50 hover:bg-emerald-500/30'}`}>
      <span className="text-lg">🤖</span>
      <div className="text-left">
        <div className={`font-bold text-sm ${threatsCount > 0 ? 'text-purple-300' : 'text-emerald-300'}`}>AI Sentry</div>
        <div className={`text-[10px] ${threatsCount > 0 ? 'text-purple-300/70' : 'text-emerald-300/70'}`}>{threatsCount > 0 ? `${threatsCount} detections` : 'All clear'}</div>
      </div>
      {threatsCount > 0 && (
        <span className="w-5 h-5 bg-purple-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">{threatsCount}</span>
      )}
    </button>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// LEGEND
// ════════════════════════════════════════════════════════════════════════════

const Legend = () => (
  <div className="bg-slate-800/90 backdrop-blur-sm border border-slate-600/50 rounded-xl p-4 space-y-3">
    <div className="text-xs font-semibold text-slate-200 uppercase tracking-wider">Legend</div>
    <div className="space-y-2">
      <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-emerald-400" /><span className="text-xs text-slate-300">Online</span></div>
      <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-red-400" /><span className="text-xs text-slate-300">Offline / Alert</span></div>
    </div>
    <div className="pt-2 border-t border-slate-600/50">
      <div className="text-[10px] font-semibold text-slate-400 uppercase mb-2">AI Status</div>
      <div className="space-y-2">
        <div className="flex items-center gap-2"><span className="text-xs">🛡️</span><span className="text-xs text-red-300">Isolated</span></div>
        <div className="flex items-center gap-2"><span className="text-xs">🔍</span><span className="text-xs text-purple-300">Analyzing</span></div>
        <div className="flex items-center gap-2"><span className="text-xs">⚠️</span><span className="text-xs text-yellow-300">Alert</span></div>
        <div className="flex items-center gap-2"><span className="text-xs">🤖</span><span className="text-xs text-emerald-300">Protected</span></div>
      </div>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const AISentry = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [edgeNodesList, setEdgeNodesList] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [threats, setThreats] = useState([]);
  const [isAIPanelOpen, setIsAIPanelOpen] = useState(false);
  const [showBriefing, setShowBriefing] = useState(true);
  const [aiBriefing, setAiBriefing] = useState(null);
  const [expandedBriefing, setExpandedBriefing] = useState(null);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [isLoadingExpanded, setIsLoadingExpanded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [stats, setStats] = useState({ totalAgents: 0, onlineAgents: 0, totalDevices: 0, alertCount: 0 });

  // Build topology
  const buildTopology = useCallback((edgeNodes, events, filterAgentId, currentThreats) => {
    const newNodes = [];
    const newEdges = [];
    const filteredEdgeNodes = filterAgentId ? edgeNodes.filter(n => n.edge_node_id === filterAgentId) : edgeNodes;
    
    const CLOUD_Y = 0, EDGE_Y = 200, DEVICE_Y_START = 420;
    const HORIZONTAL_GAP = filteredEdgeNodes.length > 4 ? 260 : filteredEdgeNodes.length > 3 ? 300 : 350;
    const DEVICE_GAP = 180;
    
    const devicesByAgent = {};
    let totalAlerts = 0;
    
    events.forEach(event => {
      const agentId = event.celerway_edge_node_id;
      const destIp = event.destination_ip;
      if (!agentId || !destIp || destIp === 'N/A') return;
      if (filterAgentId && agentId !== filterAgentId) return;
      
      if (!devicesByAgent[agentId]) devicesByAgent[agentId] = {};
      if (!devicesByAgent[agentId][destIp]) {
        devicesByAgent[agentId][destIp] = { ip: destIp, protocols: new Set(), eventCount: 0, policyViolations: 0, role: 'PLC' };
      }
      
      const device = devicesByAgent[agentId][destIp];
      device.eventCount++;
      if (event.protocol) device.protocols.add(event.protocol.toLowerCase());
      if (event.details?.policy_name && ['Warning', 'Critical', 'High'].includes(event.severity)) {
        device.policyViolations++;
        totalAlerts++;
      }
    });
    
    let totalDevices = 0;
    Object.values(devicesByAgent).forEach(d => totalDevices += Object.keys(d).length);
    
    const edgeStartX = 400 - ((filteredEdgeNodes.length - 1) * HORIZONTAL_GAP) / 2;
    const centerX = edgeStartX + ((filteredEdgeNodes.length - 1) * HORIZONTAL_GAP) / 2;
    
    newNodes.push({
      id: 'cloud', type: 'cloud', position: { x: centerX, y: CLOUD_Y },
      data: { label: 'AI Sentry Cloud', sublabel: 'AI Sentry Management', edgeCount: filteredEdgeNodes.length, assetCount: totalDevices },
    });
    
    filteredEdgeNodes.forEach((node, idx) => {
      const agentDevices = devicesByAgent[node.edge_node_id] || {};
      const deviceList = Object.values(agentDevices);
      const nodeX = edgeStartX + idx * HORIZONTAL_GAP;
      const status = getNodeStatus(node);
      
      newNodes.push({
        id: `edge-${node.edge_node_id}`, type: 'edgeGateway', position: { x: nodeX, y: EDGE_Y },
        data: { nodeId: node.edge_node_id, status, deviceCount: deviceList.length, hostname: node.hostname },
      });
      
      newEdges.push({
        id: `cloud-to-edge-${node.edge_node_id}`, source: 'cloud', target: `edge-${node.edge_node_id}`,
        type: 'smoothstep', animated: true, style: { stroke: status === 'Online' ? '#34d399' : '#f87171', strokeWidth: 2 },
      });
      
      const deviceStartX = nodeX - ((deviceList.length - 1) * DEVICE_GAP) / 2;
      
      deviceList.forEach((device, deviceIdx) => {
        const deviceId = `device-${node.edge_node_id}-${device.ip.replace(/\./g, '-')}`;
        const deviceAiStatus = getDeviceAIStatus(device.ip, currentThreats);
        
        newNodes.push({
          id: deviceId, type: 'device',
          position: { x: deviceStartX + deviceIdx * DEVICE_GAP, y: DEVICE_Y_START + (deviceIdx % 2) * 60 },
          data: { ip: device.ip, role: device.role, protocols: Array.from(device.protocols), eventCount: device.eventCount, policyViolations: device.policyViolations, aiStatus: deviceAiStatus },
        });
        
        let edgeColor = device.policyViolations > 0 ? '#f87171' : device.protocols.has('modbus') ? '#fb923c' : '#22d3ee';
        if (deviceAiStatus === AI_STATUS.ISOLATED) edgeColor = '#ef4444';
        else if (deviceAiStatus === AI_STATUS.ANALYZING) edgeColor = '#a855f7';
        
        newEdges.push({
          id: `edge-${node.edge_node_id}-to-${deviceId}`, source: `edge-${node.edge_node_id}`, target: deviceId,
          type: 'smoothstep', animated: true, style: { stroke: edgeColor, strokeWidth: deviceAiStatus !== AI_STATUS.PROTECTED ? 3 : 2 },
        });
      });
    });
    
    return { 
      nodes: newNodes, edges: newEdges, 
      stats: { totalAgents: filteredEdgeNodes.length, onlineAgents: filteredEdgeNodes.filter(n => isNodeOnline(n)).length, totalDevices, alertCount: totalAlerts }
    };
  }, []);

  // Fetch AI Briefing
  const fetchBriefing = useCallback(async () => {
    setIsLoadingAI(true);
    const briefing = await generateAIBriefing();
    setAiBriefing(briefing);
    setIsLoadingAI(false);
  }, []);

  // Fetch Expanded Briefing
  const fetchExpandedBriefing = useCallback(async () => {
    setIsLoadingExpanded(true);
    const expanded = await generateExpandedBriefing();
    setExpandedBriefing(expanded);
    setIsLoadingExpanded(false);
  }, []);

  // Request on-demand briefing
  const handleRequestBriefing = useCallback(() => {
    setShowBriefing(true);
    setAiBriefing(null);
    setExpandedBriefing(null);
    fetchBriefing();
  }, [fetchBriefing]);

  // Manual refresh for panel
  const handleManualRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const [edgeNodesResponse, eventsResponse] = await Promise.all([
        edgeNodesService.getEdgeNodes(),
        eventsService.getEvents({ limit: 500 }),
      ]);
      
      const edgeNodes = edgeNodesResponse.data || edgeNodesResponse || [];
      const events = eventsResponse.data || eventsResponse || [];
      
      setEdgeNodesList(edgeNodes);
      setAllEvents(events);
      
      const newThreats = detectThreatsHeuristic(events);
      setThreats(newThreats);
      
      const { nodes: newNodes, edges: newEdges, stats: newStats } = buildTopology(edgeNodes, events, selectedAgent, newThreats);
      setNodes(newNodes);
      setEdges(newEdges);
      setStats(newStats);
    } catch (err) {
      console.error('Refresh failed:', err);
    } finally {
      setIsRefreshing(false);
    }
  }, [buildTopology, setNodes, setEdges, selectedAgent]);

  // Handle VPN profile change approval - calls Nimbus API via backend
  const handleApproveVPN = useCallback(async (action) => {
    console.log('🌐 Executing VPN change:', action);
    
    try {
      const result = await executeNimbusAction('vpn_profile_change', {
        agent_id: action.agentId || 'Stratus5G_5728_Austin',
        current_profile: action.actionDetails?.currentProfile || 'Standard',
        new_profile: action.actionDetails?.recommendedProfile || 'Isolated-Secure',
        threat_source: action.device,
        target_devices: action.targetDevices,
        reason: action.reason
      });
      
      console.log('🌐 VPN change result:', result);
      return result;
    } catch (error) {
      console.error('🌐 VPN change failed:', error);
      return { success: false, error: error.message };
    }
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [edgeNodesResponse, eventsResponse] = await Promise.all([
        edgeNodesService.getEdgeNodes(),
        eventsService.getEvents({ limit: 500 }),
      ]);
      
      const edgeNodes = edgeNodesResponse.data || edgeNodesResponse || [];
      const events = eventsResponse.data || eventsResponse || [];
      
      setEdgeNodesList(edgeNodes);
      setAllEvents(events);
      
      const newThreats = detectThreatsHeuristic(events);
      setThreats(newThreats);
      
      const agentToUse = selectedAgent || (edgeNodes.length > 0 ? edgeNodes[4].edge_node_id : null);
      if (!selectedAgent && edgeNodes.length > 0) setSelectedAgent(agentToUse);
      
      const { nodes: newNodes, edges: newEdges, stats: newStats } = buildTopology(edgeNodes, events, agentToUse, newThreats);
      
      setNodes(newNodes);
      setEdges(newEdges);
      setStats(newStats);
      
      // Fetch AI briefing on initial load
      if (showBriefing && !aiBriefing) {
        fetchBriefing();
      }
      
    } catch (err) {
      console.error('Failed to fetch:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [buildTopology, setNodes, setEdges, selectedAgent, showBriefing, aiBriefing, fetchBriefing]);

  useEffect(() => {
    if (edgeNodesList.length > 0) {
      const { nodes: newNodes, edges: newEdges, stats: newStats } = buildTopology(edgeNodesList, allEvents, selectedAgent, threats);
      setNodes(newNodes);
      setEdges(newEdges);
      setStats(newStats);
    }
  }, [selectedAgent, edgeNodesList, allEvents, threats, buildTopology, setNodes, setEdges]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 180000);
    return () => clearInterval(interval);
  }, [fetchData]);

  return (
    <div className="h-[calc(100vh-64px)] w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-slate-900/95 to-transparent pb-8 px-6 pt-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-cyan-500/30 to-blue-600/30 rounded-xl flex items-center justify-center border border-cyan-400/30">
                <span className="text-xl">🤖</span>
              </span>
              AI Sentry
            </h1>
            <p className="text-slate-300 text-sm mt-1">Autonomous Security • Real-time AI Analysis • Proactive Protection</p>
            <div className="mt-3">
              <EdgeNodeFilter edgeNodes={edgeNodesList} selectedAgent={selectedAgent} onSelect={setSelectedAgent} />
            </div>
          </div>
          <StatsBar stats={stats} threatsCount={threats.length} onOpenPanel={() => setIsAIPanelOpen(true)} onRequestBriefing={handleRequestBriefing} />
        </div>
      </div>
      
      {/* Shift Briefing Modal */}
      <ShiftBriefingModal 
        isOpen={showBriefing && !loading}
        onClose={() => setShowBriefing(false)}
        aiBriefing={aiBriefing}
        stats={stats}
        threats={threats}
        isLoadingAI={isLoadingAI}
        onRequestExpanded={fetchExpandedBriefing}
        expandedBriefing={expandedBriefing}
        isLoadingExpanded={isLoadingExpanded}
      />
      
      {/* Proactive Alert - LEFT SIDE */}
      {!showBriefing && <ProactiveAlert threats={threats} onViewDetails={() => setIsAIPanelOpen(true)} />}
      
      {/* Loading */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/90">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin mx-auto mb-4" />
            <div className="text-cyan-300 text-lg font-medium">AI Sentry Initializing...</div>
          </div>
        </div>
      )}
      
      {/* Error */}
      {error && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/90">
          <div className="bg-red-500/20 border border-red-400/50 rounded-2xl p-8 text-center">
            <div className="text-red-300 text-lg">Failed to load: {error}</div>
          </div>
        </div>
      )}
      
      {/* React Flow */}
      {!loading && !error && (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.4 }}
          minZoom={0.2}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.7 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant="dots" gap={20} size={1} color="rgba(6, 182, 212, 0.15)" />
          <Controls className="!bg-slate-700/90 !border-slate-600/50 !rounded-xl" />
          <MiniMap 
            nodeColor={(node) => {
              if (node.type === 'cloud') return '#22d3ee';
              if (node.type === 'edgeGateway') return node.data?.status === 'Online' ? '#34d399' : '#f87171';
              if (node.data?.aiStatus === AI_STATUS.ISOLATED) return '#ef4444';
              if (node.data?.aiStatus === AI_STATUS.ANALYZING) return '#a855f7';
              return '#60a5fa';
            }}
            maskColor="rgba(15, 23, 42, 0.8)"
            className="!bg-slate-800/90 !border-slate-600/50 rounded-xl"
          />
          <Panel position="bottom-left"><Legend /></Panel>
        </ReactFlow>
      )}
      
      {/* AI Panel */}
      <AIPanel 
        isOpen={isAIPanelOpen} 
        onClose={() => setIsAIPanelOpen(false)} 
        threats={threats}
        recentEvents={allEvents}
        onRefresh={handleManualRefresh}
        isRefreshing={isRefreshing}
        onApproveVPN={handleApproveVPN}
      />
      
      <style>{`
        @keyframes slide-in-left { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slide-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in-left { animation: slide-in-left 0.3s ease-out; }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default AISentry;
